import { useEffect, useState } from "react";
import { EonUiButton } from "@eon-ui/eon-ui-components-react";
import { Icon } from "@me8eon/icons";
import type {
    ItemAction,
    ItemDetail,
    LifecycleBrief,
    LifecycleStep,
    TimelineEntry,
} from "../aidwops.types";
import { ApiError } from "../expertApi";
import { CheckIcon } from "./ExpertQueueItemDetailCard";
import "./ExpertQueue.scss";

interface IsoRequestDetailProps {
    item: ItemDetail;
    /** Does what the person pressed and answers with the item as the backend now has it. */
    onAction: (action: ItemAction, text: string) => Promise<ItemDetail>;
    /** Adds a note to the request's Jira issue. Leave out to hide the comment button. */
    onComment?: (text: string) => Promise<ItemDetail>;
    /** Re-reads the item (and the queue). Used when the item changed since it was opened (409). */
    onReload?: () => void | Promise<void>;
    onDismiss: () => void;
}

interface Failure {
    message: string;
    /** The item changed since it was opened: the same call can never succeed until it is reloaded. */
    stale: boolean;
    reasons: string[];
}

/**
 * The three stages the ISO follows, and which of the backend's lifecycle steps stands behind each.
 * By the time an item reaches the ISO, the engineer has always already approved it (that's how it got here),
 * so "engineer" is always ticked done; "iso" only ticks once the ISO actually approves.
 */
const FLOW: Array<{ key: string; label: string }> = [
    { key: "engineer", label: "Zscaler review" },
    { key: "iso", label: "ISO approval" },
    { key: "close", label: "Apply & close" },
];

const STEP_MARK: Partial<Record<LifecycleStep["state"], string>> = {
    done: "✓",
    failed: "✕",
    rejected: "✕",
};

/** The tag colour for the status pill, from how the request is going rather than from a table of statuses. */
const OUTCOME_TONE: Record<LifecycleBrief["outcome"], string> = {
    open: "medium",
    success: "low",
    staged: "context",
    rejected: "high",
    declined: "high",
    failed: "high",
    expired: "kind",
    rolled_back: "kind",
};

const IN_PROGRESS_AT_ZSCALER = [
    "approved",
    "approved_staged",
    "applying",
    "apply_failed",
];

function ShieldIcon() {
    return (
        <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="var(--eon-red)"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
        >
            <path d="M12 3 4 6v6c0 4.5 3.2 8 8 9 4.8-1 8-4.5 8-9V6l-8-3Z" />
            <line x1="12" y1="9" x2="12" y2="15" />
            <line x1="9" y1="12" x2="15" y2="12" />
        </svg>
    );
}

function formatSubmitted(isoString: string | null): string {
    if (!isoString) return "";
    const date = new Date(isoString);
    if (isNaN(date.getTime())) return "";
    return date.toLocaleString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
    });
}

function Stepper({ steps }: { steps: LifecycleStep[] }) {
    return (
        <ol
            className="iso-request-stepper"
            aria-label="Progress of this request"
        >
            {FLOW.map((stage, index) => {
                const step = steps.find((entry) => entry.key === stage.key);
                const state = step?.state ?? "upcoming";
                const meta = [
                    step?.by,
                    step?.at ? new Date(step.at).toLocaleString() : null,
                    step?.detail,
                ]
                    .filter(Boolean)
                    .join(" · ");

                return (
                    <li
                        key={stage.key}
                        className={`iso-request-step iso-request-step--${state}`}
                        aria-current={state === "current" ? "step" : undefined}
                        title={meta || undefined}
                    >
                        <span
                            className="iso-request-step-mark"
                            aria-hidden="true"
                        >
                            {STEP_MARK[state] ?? index + 1}
                        </span>
                        <span>{stage.label}</span>
                    </li>
                );
            })}
        </ol>
    );
}

function timelineWho(entry: TimelineEntry): string {
    return [entry.role, entry.by].filter(Boolean).join(" · ");
}

function timelineWhat(entry: TimelineEntry): string {
    return entry.note
        ? `${entry.title.replace(/[.!?]$/, "")}. ${entry.note}`
        : entry.title;
}

/**
 * The ISO's side of one request. What is shown and what can be pressed follow the request's status and the person's
 * role: an approver decides while it waits for them, and everything after approval is read-only.
 * Apply & close and rollback belong to the engineer queue. The backend refuses anything else with a 409 / 403.
 */
export function IsoRequestDetail({
    item,
    onAction,
    onComment,
    onReload,
    onDismiss,
}: IsoRequestDetailProps) {
    const [note, setNote] = useState("");
    // The ISO has to confirm they looked at the request before approving or rejecting; the backend asks for the same (`confirmed`).
    const [checked, setChecked] = useState(false);
    const [isBusy, setIsBusy] = useState(false);
    // Which button triggered the current request, so only that button shows a loader instead of all of them at once.
    const [pendingAction, setPendingAction] = useState<string | null>(null);
    const [fieldError, setFieldError] = useState<string | null>(null);
    const [failure, setFailure] = useState<Failure | null>(null);
    const [notice, setNotice] = useState<string | null>(null);

    useEffect(() => {
        setNote("");
        setChecked(false);
        setFieldError(null);
        setFailure(null);
        setNotice(null);
    }, [item.id]);

    const status = item.status;
    const isPending = status === "waiting_iso";
    const isAtZscaler = IN_PROGRESS_AT_ZSCALER.includes(status);

    const minReason = Math.max(item.requires?.reasonMinLength ?? 0, 12);
    const tickets = item.tickets ?? [];
    const ticket = tickets[tickets.length - 1];
    const canComment = Boolean(onComment) && tickets.length > 0;

    const statusLabel = item.statusLabel ?? status.replace(/_/g, " ");
    const summary = item.lifecycle?.summary.iso;
    const steps = item.lifecycle?.steps ?? [];
    const submitted = formatSubmitted(item.createdAt);

    const activity: TimelineEntry[] =
        item.timeline ??
        (item.audit ?? []).map((entry) => ({
            at: entry.at,
            by: entry.actor,
            role: "",
            event: entry.event,
            title: entry.event.replace(/_/g, " "),
            note: entry.detail,
        }));

    const production = item.production;
    const details: Array<{ label: string; value: string }> = [
        { label: "Requester", value: item.requester ?? "" },
        { label: "Request type", value: item.tag },
        { label: "Target", value: item.evidence?.item ?? "" },
        {
            label: "Environment",
            value:
                item.target?.environment === "DEV"
                    ? `DEV · ${item.target.tenant}`
                    : "",
        },
        { label: "Engineer approval", value: item.decision?.by ?? "" },
        // { label: "Production today", value: production?.summary ?? "" },
    ].filter((entry) => entry.value);

    const perform = async (
        actionId: string,
        work: () => Promise<unknown>,
        done: string | ((result: unknown) => string)
    ) => {
        if (isBusy) {
            return;
        }

        setIsBusy(true);
        setPendingAction(actionId);
        setFailure(null);
        setFieldError(null);
        setNotice(null);
        try {
            const result = await work();
            setNote("");
            setChecked(false);
            setNotice(typeof done === "function" ? done(result) : done);
        } catch (error) {
            setFailure({
                message: error instanceof Error ? error.message : String(error),
                stale: error instanceof ApiError && error.isStale,
                reasons: error instanceof ApiError ? (error.reasons ?? []) : [],
            });
        } finally {
            setIsBusy(false);
            setPendingAction(null);
        }
    };

    /** Runs an action that needs text of a minimum length; says so in the box when it falls short. */
    const submit = (
        actionId: string,
        action: ItemAction,
        done: string,
        min: number,
        short: string
    ) => {
        if (note.trim().length < min) {
            setFieldError(short);
            return;
        }
        void perform(actionId, () => onAction(action, note.trim()), done);
    };

    const sendComment = () => {
        if (!onComment || !note.trim()) {
            return;
        }
        void perform(
            "comment",
            () => onComment(note.trim()),
            "Comment added to the Jira issue."
        );
    };

    const commentButton = canComment && (
        <EonUiButton
            text="Add Jira comment"
            rank="secondary"
            size="small"
            disabled={isBusy || !note.trim()}
            preloader={pendingAction === "comment"}
            onClick={sendComment}
        />
    );

    const field = (label: string, placeholder: string) => (
        <>
            <label
                className="iso-request-field-label"
                htmlFor="iso-request-note"
            >
                {label}
            </label>
            <textarea
                id="iso-request-note"
                className={`expert-queue-item-detail-card-comment-input iso-request-textarea${fieldError ? " iso-request-textarea--invalid" : ""}`}
                rows={3}
                maxLength={2000}
                value={note}
                placeholder={placeholder}
                aria-invalid={fieldError ? true : undefined}
                onChange={(event) => {
                    setNote(event.target.value);
                    if (fieldError) setFieldError(null);
                }}
            />
            {fieldError && (
                <p className="queue-decision__error" role="alert">
                    {fieldError}
                </p>
            )}
        </>
    );

    let panel: JSX.Element;
    if (isPending) {
        panel = (
            <>
                <h4 className="iso-request-panel-title">ISO approval</h4>
                {field(
                    "ISO decision note · optional",
                    "Add context to the Jira issue or approval decision."
                )}
                <p className="iso-request-hint">
                    A note is needed to reject (at least {minReason}{" "}
                    characters).
                </p>
                <label className="queue-decision__confirmation">
                    <input
                        type="checkbox"
                        checked={checked}
                        disabled={isBusy}
                        onChange={(event) => setChecked(event.target.checked)}
                    />
                    I checked the scope and the pre-flight.
                </label>
                <div className="iso-request-actions">
                    {commentButton}
                    <EonUiButton
                        text="Approve"
                        rank="primary"
                        size="small"
                        disabled={isBusy || !checked}
                        preloader={pendingAction === "iso_approve"}
                        onClick={() =>
                            void perform(
                                "iso_approve",
                                () => onAction("iso_approve", note.trim()),
                                // "Your approval is recorded."
                                ""
                            )
                        }
                    />
                    <EonUiButton
                        text="Reject"
                        rank="secondary"
                        size="small"
                        disabled={isBusy || !checked}
                        preloader={pendingAction === "iso_reject"}
                        onClick={() =>
                            submit(
                                "iso_reject",
                                "iso_reject",
                                "The request is rejected.",
                                minReason,
                                `Say why you reject it: at least ${minReason} characters.`
                            )
                        }
                    />
                </div>
            </>
        );
    } else if (isAtZscaler) {
        panel = (
            <>
                <h4 className="iso-request-panel-title">
                    {status === "approved_staged"
                        ? "Approval recorded"
                        : "Zscaler approval"}
                </h4>
                {summary && <p className="iso-request-panel-text">{summary}</p>}
                {status === "apply_failed" && item.applyError && (
                    <div className="ac-alert" role="alert">
                        <p className="ac-alert-title">
                            The write to Zscaler failed.
                        </p>
                        <p className="ac-alert-detail">{item.applyError}</p>
                    </div>
                )}
                {canComment &&
                    field(
                        "Zscaler review comment · optional",
                        "Add a comment to the Jira issue."
                    )}
                <div className="iso-request-actions">{commentButton}</div>
            </>
        );
    } else {
        panel = (
            <>
                {status === "applied" && (
                    <div className="iso-request-panel--success">
                        <div className="iso-request-panel--success-header">
                            ✓ Approval Captured
                        </div>
                        <div className="iso-request-panel--success-body">
                            <span>
                                Your approval has been recorded successfully.
                            </span>
                            <span>
                                The request has now been routed to the Zscaler
                                Engineering team for implementation verification
                                and request closure.
                            </span>
                            <span>No further action is required from you.</span>
                        </div>
                    </div>
                )}
                {status !== "applied" && (
                    <>
                        <h4 className="iso-request-panel-title">
                            {status === "closed"
                                ? "Request complete"
                                : statusLabel}
                        </h4>
                        <p className="iso-request-panel-text">
                            {status === "closed"
                                ? "Request applied. The Jira issue is closed with the resolution remark in its activity."
                                : (summary ??
                                  "Nothing more will happen to this request.")}
                        </p>
                    </>
                )}
            </>
        );
    }

    return (
        <article className="expert-queue-item-detail-card">
            <div className="expert-queue-item-detail-card-header-bar">
                <button
                    type="button"
                    className="expert-queue-item-detail-card-back"
                    onClick={onDismiss}
                >
                    <Icon name="chevron_small_left" />
                    Back
                </button>

                <div className="expert-queue-item-detail-card-head">
                    <span className="expert-queue-item-detail-card-icon expert-queue-item-detail-card-icon--red">
                        <ShieldIcon />
                    </span>
                    <div className="expert-queue-item-detail-card-head-text">
                        <h3 className="expert-queue-item-detail-card-title">
                            {item.title}
                        </h3>
                        <p className="expert-queue-item-detail-card-subtitle">
                            {item.id}
                            {submitted ? ` · submitted ${submitted}` : ""}
                        </p>
                    </div>
                    <div className="expert-queue-item-detail-card-priority">
                        <span className="expert-queue-item-detail-card-priority-value">
                            {item.priority.score}
                        </span>
                        <span className="expert-queue-item-detail-card-priority-label">
                            Priority
                        </span>
                    </div>
                </div>
            </div>

            <div className="expert-queue-item-detail-card-body">
                <div className="expert-queue-item-detail-card-section">
                    <div className="iso-request-row">
                        <div className="expert-queue-item-detail-card-section-label">
                            Status
                        </div>
                        <span
                            className={`expert-queue-view-tag expert-queue-view-tag--${item.lifecycle ? OUTCOME_TONE[item.lifecycle.outcome] : "kind"}`}
                        >
                            {statusLabel}
                        </span>
                    </div>
                    {steps.length > 0 && <Stepper steps={steps} />}
                </div>

                <div className="expert-queue-item-detail-card-section">
                    <div className="expert-queue-item-detail-card-section-label">
                        Request details
                    </div>
                    {details.length > 0 && (
                        <dl className="iso-request-details">
                            {details.map((entry) => (
                                <div key={entry.label}>
                                    <dt>{entry.label}</dt>
                                    <dd>{entry.value}</dd>
                                </div>
                            ))}
                        </dl>
                    )}
                    {item.justification && (
                        <div className="iso-request-justification">
                            <strong>Business justification</strong>
                            <p>{item.justification}</p>
                        </div>
                    )}
                </div>

                {item.checks.length > 0 && (
                    <div className="expert-queue-item-detail-card-section">
                        <div className="expert-queue-item-detail-card-section-label">
                            Pre-flight checks
                        </div>
                        <ul className="expert-queue-item-detail-card-checks">
                            {item.checks.map((check) => {
                                const checkKey = check.id || check.label;
                                const icon = (
                                    <span
                                        className={`expert-queue-item-detail-card-check-icon expert-queue-item-detail-card-check-icon--${check.status}`}
                                    >
                                        <CheckIcon status={check.status} />
                                    </span>
                                );

                                return (
                                    <li
                                        key={checkKey}
                                        className={`expert-queue-item-detail-card-check expert-queue-item-detail-card-check--${check.status}`}
                                    >
                                        {check.detail ? (
                                            <details className="expert-queue-item-detail-card-check-fold">
                                                <summary className="expert-queue-item-detail-card-check-summary">
                                                    <span className="expert-queue-item-detail-card-check-summary-content">
                                                        {icon}
                                                        <span className="expert-queue-item-detail-card-check-title">
                                                            {check.label}
                                                        </span>
                                                    </span>
                                                </summary>
                                                <div className="expert-queue-item-detail-card-check-detail">
                                                    {check.detail}
                                                </div>
                                            </details>
                                        ) : (
                                            <div className="expert-queue-item-detail-card-check-summary">
                                                <span className="expert-queue-item-detail-card-check-summary-content">
                                                    {icon}
                                                    <span className="expert-queue-item-detail-card-check-title">
                                                        {check.label}
                                                    </span>
                                                </span>
                                            </div>
                                        )}
                                    </li>
                                );
                            })}
                        </ul>
                    </div>
                )}

                <div className="expert-queue-item-detail-card-section">
                    <div className="expert-queue-item-detail-card-section-label">
                        Jira ticket
                    </div>
                    {ticket ? (
                        <div className="iso-request-row">
                            <a
                                href={ticket.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="expert-queue-item-detail-card-ticket-link iso-request-ticket-key"
                            >
                                {ticket.key}
                            </a>
                            <span
                                className={`expert-queue-view-tag expert-queue-view-tag--${/closed|done|resolved/i.test(ticket.status) ? "low" : "medium"}`}
                            >
                                {ticket.status}
                            </span>
                        </div>
                    ) : (
                        <p className="expert-queue-item-detail-card-section-text">
                            No Jira issue is linked to this request yet.
                        </p>
                    )}
                    {activity.length > 0 && (
                        <ul className="iso-request-timeline">
                            {activity.map((entry, index) => (
                                <li key={`${entry.at ?? ""}-${index}`}>
                                    <div className="iso-request-timeline-who">
                                        {timelineWho(entry)}
                                    </div>
                                    <div className="iso-request-timeline-what">
                                        {timelineWhat(entry)}
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                <div className="iso-request-panel">
                    {panel}
                    {notice && (
                        <p className="iso-request-notice" role="status">
                            {notice}
                        </p>
                    )}
                    {failure && (
                        <div className="ac-alert" role="alert">
                            <p className="ac-alert-title">
                                {failure.stale
                                    ? "This request changed since you opened it."
                                    : failure.reasons.length
                                      ? "This cannot be applied as asked."
                                      : "Your change could not be saved."}
                            </p>
                            {failure.reasons.length ? (
                                <ul
                                    className="ac-alert-detail"
                                    style={{
                                        margin: "4px 0 0",
                                        paddingLeft: 18,
                                    }}
                                >
                                    {failure.reasons.map((reason) => (
                                        <li key={reason}>{reason}</li>
                                    ))}
                                </ul>
                            ) : (
                                <p className="ac-alert-detail">
                                    {failure.stale
                                        ? "Reload the request to see its current state, then try again."
                                        : failure.message}
                                </p>
                            )}
                            {failure.stale && (
                                <div>
                                    <EonUiButton
                                        text="Reload request"
                                        rank="secondary"
                                        size="small"
                                        onClick={() => {
                                            setFailure(null);
                                            if (onReload) {
                                                void onReload();
                                            } else {
                                                onDismiss();
                                            }
                                        }}
                                    />
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </article>
    );
}

export default IsoRequestDetail;
